
import { supabase } from "@/integrations/supabase/client";

export async function updateSubscriptionService(serviceData: any) {
  // Check if we have a subscription_service ID
  if (!serviceData.id) {
    console.error('Error updating subscription service: Missing id field');
    throw new Error('Missing id field for service update');
  }
  
  console.log("Updating subscription service with data:", JSON.stringify(serviceData, null, 2));
  
  // Extract the fields we don't want to update
  const { subscription_id, service_id, id, ...updates } = serviceData;
  
  // Create update object with only the fields we want to update
  const updateData = {
    status: updates.status,
    activation_date: updates.activation_date,
    termination_date: updates.termination_date,
    cancellation_date: updates.cancellation_date,
    price: updates.price
    // Removed updated_at field to let the database handle it automatically
  };
  
  console.log("Sending update with data:", JSON.stringify(updateData, null, 2));
  console.log(`Using ID: ${id} for update operation`);
  
  try {
    // Use the Supabase client with eq() but without select() to avoid &select=* in URL
    const { data, error } = await supabase
      .from('subscription_services')
      .update(updateData)
      .eq('id', id);
    
    if (error) {
      console.error('Error updating subscription service:', error);
      throw error;
    }
    
    console.log("Update successful");
    return true;
  } catch (error) {
    console.error('Exception in updateSubscriptionService:', error);
    throw error;
  }
}

export async function addSubscriptionService(serviceData: any) {
  console.log("Service data being added:", JSON.stringify(serviceData, null, 2));
  
  // Validate required fields
  if (!serviceData.subscription_id) {
    throw new Error("Missing subscription_id");
  }
  if (!serviceData.service_id) {
    throw new Error("Missing service_id");
  }
  
  // Format dates properly
  const formatDateField = (date: any) => {
    if (!date) return null;
    if (date instanceof Date) return date.toISOString().split('T')[0];
    if (typeof date === 'string') {
      // If it already looks like a proper date string, return it
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) return date;
      // Otherwise try to parse it as a date
      try {
        return new Date(date).toISOString().split('T')[0];
      } catch (e) {
        console.error("Failed to parse date:", date, e);
        return null;
      }
    }
    return null;
  };
  
  // Ensure the data is properly formatted
  const data = {
    subscription_id: serviceData.subscription_id,
    service_id: serviceData.service_id,
    // Change back to 'registered' as default status
    status: 'registered',
    registration_date: formatDateField(serviceData.registration_date) || new Date().toISOString().split('T')[0],
    activation_date: formatDateField(serviceData.activation_date),
    termination_date: formatDateField(serviceData.termination_date),
    cancellation_date: formatDateField(serviceData.cancellation_date),
    price: serviceData.price
  };
  
  console.log("Formatted data for insert:", JSON.stringify(data, null, 2));
  
  try {
    const { data: result, error } = await supabase
      .from('subscription_services')
      .insert(data)
      .select();
      
    if (error) {
      console.error('Error adding subscription service:', error);
      throw error;
    }
    
    console.log("Service added successfully:", result);
    return true;
  } catch (error) {
    console.error('Exception in addSubscriptionService:', error);
    throw error;
  }
}

export async function removeSubscriptionService(subscriptionId: string, serviceId: string, userEmail: string) {
  console.log(`Removing service ${serviceId} from subscription ${subscriptionId}`);
  
  const { data, error } = await supabase
    .from('subscription_services')
    .delete()
    .eq('subscription_id', subscriptionId)
    .eq('service_id', serviceId)
    .select();
    
  if (error) {
    console.error('Error removing subscription service:', error);
    throw error;
  }
  
  console.log("Service removed successfully:", data);
  return true;
}
